import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useTiers } from "~/api/swr/useTiers";
import { type TierDetails } from "~/api/types";
import { updateTierOrder } from "~/api/v2/requests";
import { useDocuments } from "~/api/v2/useDocuments";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { SortableReorderList } from "~/molecules/SortableReorderList";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReorderTierModal = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const { data: tiers, mutate: mutateTiers } = useTiers();
  const { mutate: mutateDocuments } = useDocuments();
  const [isSaving, setIsSaving] = useState(false);
  const [tiersState, setTiersState] = useState<TierDetails[] | null>(null);
  const reorderedTiers = tiersState ?? tiers ?? [];

  const originalTierIds = tiers?.map(({ id }) => id) ?? [];
  const reorderedTierIds = reorderedTiers.map(({ id }) => id);

  const hasChanges = originalTierIds.some(
    (tierId, index) => reorderedTierIds[index] !== tierId,
  );

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await updateTierOrder(reorderedTierIds);
      void mutateTiers();
      void mutateDocuments();
      toast.success(t(($) => $.toasts.success.tierReordered));
      setTiersState(null);
      onOpenChange(false);
    } catch {
      toast.error(t(($) => $.toasts.error.tierNotReordered));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setTiersState(null);
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t(($) => $.commons.buttons.reorderTiers)}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <SortableReorderList
            items={reorderedTiers}
            onChange={setTiersState}
            sortableType="TIER_ROW"
          />
        </div>
        <DialogFooter className="shrink-0">
          <Button
            disabled={!hasChanges || isSaving}
            onClick={() => void handleSave()}
          >
            {isSaving
              ? t(($) => $.commons.buttons.saving)
              : t(($) => $.commons.buttons.save)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
